import { micProblemChange } from './rules'
import type { SoundcheckTelemetry } from './telemetry'
import type { MicCheckHost, MicFailure, MicReport } from './types'

/**
 * Logs the microphone problem of a host that has no SoundcheckController (the
 * AV check), using the meeting's microphone-layer rules: a failure opens it, a
 * changed cause re-logs it, ready resolves it. Every problem it opens ends in
 * a soundcheck_incident_ended row, 'recovered' when the mic comes back and
 * 'left' when the host tears the check down with it still open (the student
 * joined, left, or picked another preview device, which gets its own log).
 */
export class PreviewMicProblemLog {
  private openCause: MicFailure | null = null
  private openedAt = 0
  private disposed = false

  constructor(
    private readonly telemetry: SoundcheckTelemetry,
    private readonly host: MicCheckHost,
    // The causes the host explains on screen, for the row's `shown`.
    private readonly shownCauses: MicFailure[],
    private readonly now: () => number
  ) {}

  onReport(report: MicReport): void {
    if (this.disposed) return
    const change = micProblemChange(this.openCause, report)
    if (change === null) return
    const now = this.now()
    if (change.kind === 'opened') {
      if (this.openCause === null) this.openedAt = now
      this.openCause = change.cause
      const shown = this.shownCauses.includes(change.cause)
      this.telemetry.problem('microphone', change.cause, shown, {}, this.host)
      return
    }
    const secondsOpen = (now - this.openedAt) / 1000
    this.telemetry.resolved(
      'microphone',
      change.cause,
      secondsOpen,
      'mic_ready',
      this.host
    )
    this.telemetry.incidentEnded('recovered', secondsOpen, '', this.host)
    this.openCause = null
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    if (this.openCause === null) return
    const secondsOpen = (this.now() - this.openedAt) / 1000
    this.telemetry.incidentEnded('left', secondsOpen, this.openCause, this.host)
  }
}
